interface SignUpData {
  name: string;
  email: string;
  phone?: string;
  password: string;
}

interface SignInData {
  email: string;
  password: string;
}

interface RecoveryData {
  email: string;
}

interface ResetPasswordData {
  token: string;
  password: string;
}

interface UpdatePasswordData {
  currentPassword: string;
  newPassword: string;
}

interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  token?: string;
}

class AuthService {
  private baseUrl: string;

  constructor(baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3030') {
    this.baseUrl = baseUrl;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${endpoint}`;

    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Add auth token if available
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers = {
        ...config.headers,
        'Authorization': `Bearer ${token}`,
      };
    }

    try {
      const response = await fetch(url, config);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        ...data,
      };
    } catch (error) {
      console.error('API request failed:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Network error occurred',
      };
    }
  }

  // Registrar novo usuário
  async signup(data: SignUpData): Promise<ApiResponse> {
    return this.request('/auth/signup', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Fazer login
  async signin(data: SignInData): Promise<ApiResponse> {
    const response = await this.request('/auth/signin', {
      method: 'POST',
      body: JSON.stringify(data),
    });

    // Salvar token se login foi bem sucedido
    if (response.success && response.token) {
      localStorage.setItem('authToken', response.token);
      localStorage.setItem('user', JSON.stringify(response.data));
    }

    return response;
  }

  // Recuperar senha
  async recoverPassword(data: RecoveryData): Promise<ApiResponse> {
    return this.request('/auth/recovery', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Redefinir senha com token
  async resetPassword(data: ResetPasswordData): Promise<ApiResponse> {
    return this.request('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Atualizar senha (usuário logado)
  async updatePassword(data: UpdatePasswordData): Promise<ApiResponse> {
    return this.request('/auth/password', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Fazer logout
  async signout(): Promise<ApiResponse> {
    const response = await this.request('/auth/signout', {
      method: 'POST',
    });

    // Limpar dados locais independente da resposta
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');

    return response;
  }

  // Obter perfil do usuário
  async getProfile(): Promise<ApiResponse> {
    return this.request('/auth/profile', {
      method: 'GET',
    });
  }

  // Verificar se usuário está logado
  isAuthenticated(): boolean {
    return !!localStorage.getItem('authToken');
  }

  // Obter dados do usuário do localStorage
  getCurrentUser() {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  }

  // Limpar dados de autenticação
  clearAuthData(): void {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
  }
}

const authService = new AuthService();
export default authService;